/**
 * Settlement history per wallet (#788): what the session's fills show, the settledOutcome answers, and a local cache
 * (no backend).
 *
 *   - Settlement fills come from the user session (account/sessionFills.ts, #1809 W8): withFills adds them to the store.
 *     The store keeps them over visits, so a returning wallet has its rows at once.
 *   - settledOutcome per settled outcome id: a non-null answer is final, so it is read once (Victor, #1809). Null is not
 *     an answer: it is retried after NULL_RETRY_MS, doubling per null answer up to NULL_RETRY_MAX_MS, and at most
 *     MAX_NULL_RETRIES_PER_REFRESH per refresh. Answers already cached are kept as they are, whenever they were read.
 *   - Pacing (#1809): one lookup per settled outcome can pass Hyperliquid's 1200/min per IP, so the app runs the lookups
 *     through the client's background pacer (hl/weightPacer.ts). `onProgress` gets the store after each answer, so rows
 *     fill in as they arrive. `keepGoing` false ends a refresh early with `incomplete` set: what it read is kept, and the
 *     next refresh picks up every lookup still due.
 *   - Cache: localStorage `hip4.settlements.<env>.<address>` (v1). Every access is wrapped: a private window or
 *     blocked storage just means no cache. Caches written before W8 also hold `scannedTo`, which nothing reads now.
 *   - Reach (#894): `truncated` (historyTruncated) records that older settlements may be missing: the session's download
 *     came back full (`capped`), or the cache cap dropped some.
 */
import type { EnvName } from '../config/env';
import { abortError, type InfoClient, type RequestOptions } from '../hl/info';
import type { WeightPacer } from '../hl/weightPacer';
import {
  historyTruncated,
  mergeSettlementFills,
  parseSettledOutcome,
  type SettledOutcome,
  type SettlementFill,
} from './parse';

export const SETTLEMENT_STORAGE_PREFIX = 'hip4.settlements.';
/** First retry of a null settledOutcome answer. Doubles per null answer: some settled outcomes answer null for good. */
export const NULL_RETRY_MS = 15 * 60_000;
/** Longest wait between retries of a null answer. */
export const NULL_RETRY_MAX_MS = 86_400_000;
/** Null answers retried per refresh, oldest first; the rest wait for the next refresh. First lookups are not capped. */
export const MAX_NULL_RETRIES_PER_REFRESH = 10;
/** Shortest wait before the next refresh that a due lookup asks for: a failed one, or a null past its cap. */
export const LOOKUP_RETRY_MS = 5 * 60_000;
/** Rows of one userFills / userFillsByTime answer, and how far back settlements are looked for (Victor, #1809). */
export const FILLS_PAGE_CAP = 2000;
/** Weight of one settledOutcome. */
export const SETTLED_OUTCOME_WEIGHT = 20;
/** Weight of a userFills / userFillsByTime / historicalOrders read before its rows; each FILL_ROWS_PER_WEIGHT rows add 1. */
export const FILL_BASE_WEIGHT = 20;
export const FILL_ROWS_PER_WEIGHT = 20;
/** Most recent settlement fills kept in the cache. */
export const CACHE_MAX_FILLS = 1000;

export interface CachedOutcome {
  /** Normalized response (null = Hyperliquid answered null / not settled yet). */
  s: SettledOutcome | null;
  /** ms when it was fetched. */
  at: number;
  /** Null answers in a row. Absent on an answer, and in caches written before it (counts as 1). */
  nulls?: number;
}

export interface SettlementStore {
  v: 1;
  fills: SettlementFill[];
  outcomes: Record<string, CachedOutcome>;
  /** Older settlements may be missing (#894). Absent in caches written before it = false. */
  truncated?: boolean;
  /** A refresh is still running or was cut short: fills or answers may be missing, so the next look refreshes at once. */
  incomplete?: boolean;
}

export const emptyStore = (): SettlementStore => ({ v: 1, fills: [], outcomes: {} });

export function settlementStorageKey(env: EnvName, user: string): string {
  return `${SETTLEMENT_STORAGE_PREFIX}${env}.${user.toLowerCase()}`;
}

function storage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadStore(env: EnvName, user: string): SettlementStore | null {
  try {
    const raw = storage()?.getItem(settlementStorageKey(env, user));
    if (!raw) return null;
    const x = JSON.parse(raw) as Partial<SettlementStore>;
    if (x.v !== 1 || !Array.isArray(x.fills) || !x.outcomes || typeof x.outcomes !== 'object') {
      return null;
    }
    return x as SettlementStore;
  } catch {
    return null;
  }
}

export function saveStore(env: EnvName, user: string, s: SettlementStore): void {
  try {
    storage()?.setItem(settlementStorageKey(env, user), JSON.stringify(s));
  } catch {
    // Quota / blocked storage: the next visit simply re-fetches.
  }
}

/** `prev` with the session's settlement fills added. `rows`: fills the session holds, `capped`: older ones exist. */
export function withFills(prev: SettlementStore, held: readonly SettlementFill[], rows: number, capped: boolean): SettlementStore {
  const merged = mergeSettlementFills(prev.fills, held);
  const fills = merged.slice(0, CACHE_MAX_FILLS);
  return { ...prev, fills, truncated: historyTruncated(prev.truncated === true || capped, rows, merged.length - fills.length) };
}

export async function fetchSettledOutcome(info: InfoClient, outcome: number, opts: RequestOptions = {}): Promise<SettledOutcome | null> {
  const raw = await info.request<unknown>({ type: 'settledOutcome', outcome }, opts);
  return parseSettledOutcome(raw, outcome);
}

/** Wait after the `nulls`th null answer in a row. */
export function nullRetryMs(nulls: number): number {
  return Math.min(NULL_RETRY_MAX_MS, NULL_RETRY_MS * 2 ** Math.max(0, nulls - 1));
}

/** Does this outcome need a lookup at `now`? A non-null answer is final (#1809). */
export function needsLookup(c: CachedOutcome | undefined, now: number): boolean {
  if (!c) return true;
  return c.s === null && now - c.at >= nullRetryMs(c.nulls ?? 1);
}

/**
 * How long until the next lookup falls due, false when none will. A null answer waits out its back-off and a failed
 * lookup LOOKUP_RETRY_MS, so nothing is scheduled for a wallet with every answer read.
 */
export function nextLookupIn(s: SettlementStore, now: number): number | false {
  let soonest = Infinity;
  for (const id of new Set(s.fills.map((f) => f.outcome))) {
    const c = s.outcomes[String(id)];
    if (!c) soonest = 0;
    else if (c.s === null) soonest = Math.min(soonest, c.at + nullRetryMs(c.nulls ?? 1) - now);
  }
  return soonest === Infinity ? false : Math.max(LOOKUP_RETRY_MS, soonest);
}

export interface RefreshOptions extends RequestOptions {
  /** Pays for each request. Without one nothing waits. */
  pacer?: WeightPacer;
  /** Asked by the pacer before each request; false stops early. Only used with a pacer. */
  keepGoing?: () => boolean;
  now?: number;
  /** Parallel settledOutcome lookups (default 4). */
  concurrency?: number;
  /** Called with the store so far, marked `incomplete`, after each answer. */
  onProgress?: (s: SettlementStore) => void;
}

/** The settledOutcome lookups due for `prev`, newest fill first. */
export async function refreshStore(info: InfoClient, prev: SettlementStore, opts: RefreshOptions = {}): Promise<SettlementStore> {
  const now = opts.now ?? Date.now();
  const { signal, pacer, keepGoing, onProgress } = opts;
  const { fills, truncated } = prev;

  const outcomes: Record<string, CachedOutcome> = {};
  const due: number[] = [];
  const nullRetries: number[] = [];
  for (const id of new Set(fills.map((f) => f.outcome))) {
    const c = prev.outcomes[String(id)];
    if (c) outcomes[String(id)] = c;
    if (needsLookup(c, now)) (c ? nullRetries : due).push(id);
  }
  // First lookups go first, then the oldest null answers: a run cut short has spent its budget on what shows the most.
  nullRetries.sort((a, b) => prev.outcomes[String(a)]!.at - prev.outcomes[String(b)]!.at);
  due.push(...nullRetries.slice(0, MAX_NULL_RETRIES_PER_REFRESH));
  const store = (incomplete: boolean): SettlementStore => ({
    v: 1,
    fills,
    outcomes: { ...outcomes },
    truncated,
    ...(incomplete ? { incomplete } : {}),
  });
  let next = 0;
  let stopped = false;
  const worker = async () => {
    while (next < due.length && !stopped) {
      const id = due[next++]!;
      if (pacer && !(await pacer.take(SETTLED_OUTCOME_WEIGHT, { signal, keepGoing }))) {
        stopped = true;
        return;
      }
      try {
        const s = await fetchSettledOutcome(info, id, { signal });
        const at = opts.now ?? Date.now();
        const was = prev.outcomes[String(id)];
        outcomes[String(id)] = s === null ? { s, at, nulls: (was ? (was.nulls ?? 1) : 0) + 1 } : { s, at };
      } catch {
        // A failed lookup keeps the previous answer (if any) and is retried next time.
        continue;
      }
      if (!signal?.aborted) onProgress?.(store(true));
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, opts.concurrency ?? 4) }, worker));
  if (signal?.aborted) throw abortError();
  return store(stopped);
}
